import React, { useRef, useState, useEffect, useMemo } from "react";
import ReactWordcloud from "react-wordcloud";
import "tippy.js/dist/tippy.css"; // estilos del tooltip (tippy.js)
import "tippy.js/animations/scale.css"; // animación de aparición del tooltip
import { COLORES_NUBE } from "../helper";

/* -------------------------------------------------------------------------- */
/*  Nube de palabras (Análisis de información) con react-wordcloud.           */
/*  Diseño: palabras horizontales, tamaño según frecuencia y colores variados */
/*  (la más frecuente toma el primer color de la paleta -> dorado).           */
/*  Es responsiva: el tamaño de fuente y el alto se calculan a partir del     */
/*  ancho disponible en pantalla.                                             */
/* -------------------------------------------------------------------------- */

const FUENTE = "Red Hat Display, sans-serif";

export default function NubePalabras({ palabras = [] }) {
  const contenedorRef = useRef(null);
  const [ancho, setAncho] = useState(0);

  // Medir el ancho disponible (redondeado en pasos de 40px para no recalcular
  // el layout en cada píxel al redimensionar).
  useEffect(() => {
    const medir = () => {
      if (contenedorRef.current) {
        setAncho(Math.round(contenedorRef.current.offsetWidth / 40) * 40);
      }
    };
    medir();
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, []);

  // Ordenadas desc: la más frecuente queda primera -> react-wordcloud le asigna
  // el primer color de la paleta (dorado).
  const ordenadas = useMemo(() => [...palabras].sort((a, b) => b.value - a.value), [palabras]);

  // Tamaño de fuente y alto proporcionales al ancho (responsivo, con topes).
  const maxFont = Math.max(30, Math.min(70, Math.round(ancho / 12)));
  const minFont = Math.max(11, Math.round(maxFont * 0.26));
  const alto = Math.max(240, Math.min(430, Math.round(ancho * 0.5)));

  const options = useMemo(
    () => ({
      colors: COLORES_NUBE,
      enableTooltip: true,
      // Tooltip: "Palabra (nº de veces)"
      getWordTooltip: (word) => `${word.text} (${word.value})`,
      // Animación de aparición del tooltip (tippy.js): zoom + fade
      tooltipOptions: { animation: "scale", duration: [200, 150] },
      deterministic: true, // layout reproducible (no "salta" entre renders)
      fontFamily: FUENTE,
      fontSizes: [minFont, maxFont],
      fontWeight: "700",
      padding: 3,
      rotations: 1,
      rotationAngles: [0, 0], // todo horizontal
      scale: "linear",
      spiral: "archimedean",
      transitionDuration: 400,
    }),
    [minFont, maxFont]
  );

  if (!ordenadas.length) return null;

  // react-wordcloud llena el contenedor padre (ancho y alto).
  return (
    <div ref={contenedorRef} style={{ width: "100%", height: alto }}>
      {ancho > 0 && <ReactWordcloud words={ordenadas} options={options} />}
    </div>
  );
}
